import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getJrSummary } from '../services/jrOverview.js';
import { usePolicy } from '../components/PolicyContext.jsx';

// Společná čísla (JCH-Cisla): the same numbers the JR sees, framed for the owner.
const SEMAFOR = {
  green: { label: 'zelená', cls: 'semafor semafor--grn' },
  orange: { label: 'oranžová', cls: 'semafor semafor--org' },
  red: { label: 'červená', cls: 'semafor semafor--red' },
};

const czk = (n) => `${Math.round(Number(n) || 0).toLocaleString('cs-CZ')} Kč`;
const dt = (v) => (v ? new Date(v).toLocaleDateString('cs-CZ') : '—');

const NA_TOBE = (policy) => [
  ['Nábor a uvítání.', 'Po podpisu smlouvy pošleš JR jméno, e-mail, telefon a termín kurzu – klidně na poslední chvíli před začátkem školení.'],
  ['Příprava je tvoje.', 'S nováčky jsi na školeních aspoň jednou týdně a mezi řečí jim připomínáš zápis. Huddle ani review s JR v přípravě nejsou.'],
  ['Nesložená zkouška:', `makléř jde na další termín (pokusů bez limitu) a mezi pokusy ho dál vedeš ty, s plánem ${policy.operations.preparationWeeklyContacts} oslovení. Složí-li do 3 týdnů od neúspěšného pokusu, zůstává v původní skupině, jinak jde do další – zařazení potvrzuješ ty nebo JR.`],
  ['Pevné body:', `bránové schůzky (po zkoušce, v 6. a ve 12. měsíci), 1:1 měsíčně do 6. měsíce a pak čtvrtletně, čtvrtletní schůzka se Samostatným a měsíční půlhodina s JR po ${policy.progression.readyDay}.`],
  ['Milníky:', `hovor do ${policy.operations.milestoneCallHours} hodin, u samostatnosti a milníku ${czk(policy.goals.millionMilestone)} osobní setkání.`],
  ['Zpětná vazba na JR:', 'makléři ti ji dávají na 1:1, probereš ji s JR na měsíční půlhodině.'],
  ['4 týdny v červené:', 'po e-mailu si do týdne dáš 15 minut s JR a dohodnete jednu věc na další 4 týdny.'],
  ['Odchod:', 'o odchodu makléře řekneš JR v den rozhodnutí a jeho zakázky rozdělíš. Kdo neodklikne podmínky Drillu, řešíš s ním osobně.'],
];

const MENIS = [
  'Běžně zapisuješ potvrzení milníkového hovoru, poznámku a jeden cíl ze své schůzky.',
  'Můžeš změnit zápis úrovně. Zapisuje ho JR jednou měsíčně po uzavření čísel, při neshodě rozhoduješ ty.',
  'Parametry měníte s JR společně. Nastavení platí od zvoleného budoucího dne; historie se nemění.',
];

export default function Cisla() {
  const { policy } = usePolicy();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    getJrSummary()
      .then(setData)
      .catch((e) => setError(e.response?.data?.error || e.message || 'Společná čísla se nepodařilo načíst.'));
  }, []);

  if (error) return <p className="dashboard__error" role="alert">{error}</p>;
  if (!data) return <div className="dashboard__status">Načítá se…</div>;

  const t = data.totals;
  const maklers = data.maklers || [];
  const levels = policy.levels.map((l) => l.name).filter((l) => t.by_level[l])
    .map((l) => `${t.by_level[l]} ${l.toLowerCase()}`).join(' · ');
  const fast = maklers.filter((m) => Number(m.tempo_czk) >= policy.goals.annualTempo);
  const month = new Date().toLocaleDateString('cs-CZ', { month: 'long', year: 'numeric' });

  return (
    <section className="dashboard">
      <div className="dashboard__header">
        <div>
          <h1 className="dashboard__title">Společná čísla · {month}</h1>
          <p className="dashboard__lead">
            Stejná obrazovka, jakou vidí JR. Týdenní report z uzavřeného týdne ti navíc chodí
            každý čtvrtek e-mailem a na Disk.
          </p>
        </div>
        <div className="dashboard__status">
          Stav k {new Date(data.generated_at).toLocaleDateString('cs-CZ')}
          <div><span className="semafor semafor--org">Provize: poslední import {dt(data.last_import)}</span></div>
        </div>
      </div>

      <div className="dashboard__cards">
        <article className="card">
          <span className="card__caption">Makléřů v Drillu</span>
          <span className="card__big">{t.count}</span>
          <span className="card__hint">{levels || '—'}</span>
        </article>
        <article className="card">
          <span className="card__caption">Semafor minulý týden</span>
          <div className="card__semas">
            <span className="semafor semafor--grn">{t.semafor.green} zelená</span>
            <span className="semafor semafor--org">{t.semafor.orange} oranžová</span>
            <span className="semafor semafor--red">{t.semafor.red} červená</span>
          </div>
          <span className="card__hint">
            Jen z povinných oslovení, vůči vlastnímu plánu, bez žebříčku. V přípravě proti {policy.operations.preparationWeeklyContacts}.
            Úroveň 4 je bez povinného standardu – od zápisu úrovně.
          </span>
        </article>
        <article className="card">
          <span className="card__caption">Vyplacené provize</span>
          <span className="card__big">{czk(t.commission_month)}</span>
          <span className="card__hint">tento měsíc · nahráno do {dt(data.last_import)}</span>
        </article>
        <article className="card">
          <span className="card__caption">Na cílovém tempu {czk(policy.goals.annualTempo)}</span>
          <span className="card__big">{fast.length} z {t.count}</span>
          <span className="card__hint">{fast.map((m) => m.name).join(', ') || '—'}</span>
        </article>
      </div>

      <div className="card jr-table-wrap">
        <table className="jr-table" aria-label="Makléři">
          <thead>
            <tr>
              <th>Makléř</th>
              <th>Fáze</th>
              <th>Semafor</th>
              <th>Nábory 90 dní</th>
              <th>Tempo · rozjednáno</th>
              <th>Za 12 měs.</th>
              <th>Můj nejbližší krok</th>
            </tr>
          </thead>
          <tbody>
            {maklers.map((m) => (
              <tr key={m.id}>
                <td><Link to={`/dashboard/database/${m.id}`}>{m.name}</Link></td>
                <td>
                  <span style={{ color: m.level_color }}>{m.level}</span>
                  {m.cohort && <div className="card__hint">{m.cohort}</div>}
                </td>
                <td>
                  {m.semafor ? <span className={SEMAFOR[m.semafor].cls}>{SEMAFOR[m.semafor].label}</span>
                    : <span className="semafor semafor--none">—</span>}
                  {m.current_week && (
                    <div className="card__hint">
                      {m.current_week.done} / {m.current_week.plan}
                      {m.current_week.plan ? ` · ${Math.round((100 * m.current_week.done) / m.current_week.plan)} %` : ''}
                    </div>
                  )}
                </td>
                <td>{m.acquisitions_90d}</td>
                <td>
                  {czk(m.tempo_czk)}
                  <div className="card__hint">odhad rozj. {m.rozjednano == null ? 'bez provizních dat' : czk(m.rozjednano)}</div>
                </td>
                <td>{czk(m.commission_12m)}</td>
                <td>{m.next_event ? `${dt(m.next_event.date)} · ${m.next_event.title}` : '—'}</td>
              </tr>
            ))}
            {maklers.length === 0 && (
              <tr><td colSpan={7}>Zatím nikdo.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="card__hint">
        Rozjednáno = aktivní zakázky k prodeji × {czk(policy.conversion.saleCommission)} × {policy.conversion.saleCloseRate}.
        Jde o modelový odhad, nikoli vyplacený příjem. Odhad z nájmů čeká na potvrzení pravidla konverze. Upozornění na makléře bez
        schůzky nebo bez náboru jde JR a do reportu, barvu nemění. Konverzi oslovení → schůzka
        vidíte jen ty a JR – makléř ji nevidí.
      </p>
      {data.model && <section className="card">
        <h2 className="card__title">Model a konverze · orientační odhad</h2>
        <p>{data.model.contacts_weekly} oslovení týdně → {data.model.meetings_weekly.toLocaleString('cs-CZ')} schůzek týdně.
          {' '}Roční model provizí: {czk(data.model.annual_commission)}
          {' '}(citlivost {czk(data.model.annual_commission_low)} až {czk(data.model.annual_commission_high)}).</p>
        <p>Nábor → prodej {data.model.acquisition_to_sale_months} měsíců,
          {' '}obchod → výplata {data.model.sale_to_payout_months} měsíců.
          {' '}Model zahrnuje {data.model.rentals_yearly} nájmů ročně.
          {' '}Srovnání zajetého makléře: {czk(data.experienced_model.annual_commission)} ročně.</p>
        <p>Standard JCH pro srovnání: {data.model.comparison_meetings_weekly} schůzek týdně,
          {' '}{data.model.comparison_acquisitions_monthly} nábory měsíčně,
          {' '}přibližně {data.model.comparison_active_listings} aktivních zakázek.</p>
        <p className="card__hint">Model neurčuje úroveň ani nepřepočítává samostatně nastavené orientační cíle plánu.</p>
      </section>}

      <div className="jr-two">
        <section className="card">
          <h2 className="card__title">Co je na tobě</h2>
          <p className="card__hint">Denní vedení je na JR. Ty máš tyhle věci:</p>
          <ul className="jr-list">
            {NA_TOBE(policy).map(([b, t2]) => <li key={b}><strong>{b}</strong> {t2}</li>)}
          </ul>
          <div className="jr-pills">
            <Link className="button button--secondary" to="/dashboard/milniky">Milníky a pevné body</Link>{' '}
            <Link className="button button--secondary" to="/dashboard/report">Týdenní report</Link>{' '}
            <Link className="button button--secondary" to="/dashboard/kpi">KPI systému</Link>
          </div>
        </section>
        <section className="card">
          <h2 className="card__title">Co tady měníš</h2>
          <p className="card__hint">
            Čteš všechno a máš stejná správcovská práva jako JR – oba máte manažerský přístup a
            zastupujete se. Stejný přístup může dostat i další člověk.
          </p>
          <ul className="jr-list">{MENIS.map((x) => <li key={x}>{x}</li>)}</ul>
          <p className="card__hint">
            Drill v první verzi nezapisuje do tvého kalendáře. Pevné body ukazuje a upozorňuje
            na ně e-mailem.
          </p>
          <Link className="button button--secondary" to="/dashboard/level-assignment">Zařazení do úrovně</Link>{' '}
          <Link className="button button--secondary" to="/dashboard/parametry">Parametry</Link>
        </section>
      </div>
    </section>
  );
}
